import React, { useEffect, useMemo, useState } from 'react';
import { IonSpinner, IonIcon } from '@ionic/react';
import {
  searchOutline,
  warningOutline,
  chevronForwardOutline,
  chevronDownOutline,
  refreshOutline,
} from 'ionicons/icons';
import { orderService } from '../services/api';
import { Order } from '../types';

// ── GBP conversion (mirrors Orders.tsx) ──────────────────────────────────────
// Sale amounts are stored in the order's own currency in `totalPriceGBP`; `fxRate` is the
// currency→GBP rate locked at import time. Revenue is always shown converted to GBP.
const FX_TO_GBP: Record<string, number> = { GBP: 1, EUR: 0.85, USD: 0.79 };
const rateFor = (o: Order): number =>
  Number((o as any).fxRate) || FX_TO_GBP[(o as any).currency as string] || 1;
const revenueGBP = (o: Order): number => (Number(o.totalPriceGBP) || 0) * rateFor(o);

// UI-only platform relabelling (matches Orders/Listings).
const PLATFORM_LABEL: Record<string, string> = { LiveFootballTickets: 'Live' };
const toPlatformLabel = (v: string): string => PLATFORM_LABEL[v] ?? (v || 'Unknown');

// Stable colour per platform for the little badges.
const PLATFORM_COLOR: Record<string, string> = {
  Live: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  FTN: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  HelloTickets: 'bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300',
  StubHub: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  Vivid: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
};
const platformColor = (p: string): string =>
  PLATFORM_COLOR[p] ?? 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';

const fmtInt = (n: number): string => n.toLocaleString('en-GB');
const fmtGBP = (n: number): string =>
  '£' + n.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

type PlatformStat = { tickets: number; revenue: number; orders: number };
type EventStat = {
  eventName: string;
  tickets: number;
  revenue: number;
  orders: number;
  byPlatform: Record<string, PlatformStat>;
};

type SortKey = 'tickets' | 'revenue';

const THRESHOLD_KEY = 'eventSales.ticketThreshold';

const EventSalesContent: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('tickets');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [threshold, setThreshold] = useState<number>(() => {
    const saved = Number(localStorage.getItem(THRESHOLD_KEY));
    return Number.isFinite(saved) && saved > 0 ? saved : 50;
  });

  useEffect(() => {
    localStorage.setItem(THRESHOLD_KEY, String(threshold));
  }, [threshold]);

  const load = async () => {
    try {
      setError(null);
      setLoading(true);
      setOrders(await orderService.getAll());
    } catch (e: any) {
      setError(e?.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  // Aggregate orders → per-event stats with a per-platform breakdown.
  const events = useMemo<EventStat[]>(() => {
    const map = new Map<string, EventStat>();
    for (const o of orders) {
      const name = (o.eventName || '').trim() || 'Unknown event';
      const platform = toPlatformLabel(String(o.platform ?? '').trim());
      const qty = Number(o.quantity) || 0;
      const rev = revenueGBP(o);
      let ev = map.get(name);
      if (!ev) {
        ev = { eventName: name, tickets: 0, revenue: 0, orders: 0, byPlatform: {} };
        map.set(name, ev);
      }
      ev.tickets += qty;
      ev.revenue += rev;
      ev.orders += 1;
      const ps = (ev.byPlatform[platform] ??= { tickets: 0, revenue: 0, orders: 0 });
      ps.tickets += qty;
      ps.revenue += rev;
      ps.orders += 1;
    }
    return Array.from(map.values());
  }, [orders]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? events.filter((e) => e.eventName.toLowerCase().includes(q))
      : events;
    return [...filtered].sort((a, b) => b[sortKey] - a[sortKey]);
  }, [events, search, sortKey]);

  const totals = useMemo(() => {
    const t = visible.reduce(
      (acc, e) => {
        acc.tickets += e.tickets;
        acc.revenue += e.revenue;
        acc.orders += e.orders;
        return acc;
      },
      { tickets: 0, revenue: 0, orders: 0 },
    );
    const over = visible.filter((e) => e.tickets >= threshold).length;
    return { ...t, over };
  }, [visible, threshold]);

  const toggle = (name: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });

  // Row colour by how close ticket count is to the alert threshold.
  const rowTone = (tickets: number): string => {
    if (tickets >= threshold)
      return 'border-l-4 border-red-500 bg-red-50/60 dark:bg-red-900/20';
    if (tickets >= threshold * 0.8)
      return 'border-l-4 border-amber-400 bg-amber-50/50 dark:bg-amber-900/15';
    return 'border-l-4 border-transparent';
  };
  const ticketTextTone = (tickets: number): string => {
    if (tickets >= threshold) return 'text-red-600 dark:text-red-400';
    if (tickets >= threshold * 0.8) return 'text-amber-600 dark:text-amber-400';
    return 'text-gray-900 dark:text-gray-100';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <IonSpinner name="crescent" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6 text-center text-red-600 dark:text-red-400">
        {error}
        <div className="mt-3">
          <button
            onClick={load}
            className="px-3 py-1.5 rounded bg-blue-600 text-white text-sm"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 max-w-5xl mx-auto">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="relative flex-1 min-w-[180px]">
          <IonIcon
            icon={searchOutline}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search event…"
            className="w-full pl-8 pr-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm"
          />
        </div>

        <div className="flex items-center gap-1.5 text-sm">
          <span className="text-gray-500 dark:text-gray-400">Alert ≥</span>
          <input
            type="number"
            min={1}
            value={threshold}
            onChange={(e) => setThreshold(Math.max(1, Number(e.target.value) || 1))}
            className="w-20 px-2 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-right"
          />
          <span className="text-gray-500 dark:text-gray-400">tickets</span>
        </div>

        <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-700 overflow-hidden text-sm">
          {(['tickets', 'revenue'] as SortKey[]).map((k) => (
            <button
              key={k}
              onClick={() => setSortKey(k)}
              className={
                'px-3 py-2 ' +
                (sortKey === k
                  ? 'bg-blue-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300')
              }
            >
              {k === 'tickets' ? 'Tickets' : 'Revenue'}
            </button>
          ))}
        </div>

        <button
          onClick={load}
          title="Refresh"
          className="p-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300"
        >
          <IonIcon icon={refreshOutline} />
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        <div className="rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-3">
          <div className="text-xs text-gray-500 dark:text-gray-400">Events</div>
          <div className="text-lg font-semibold">{fmtInt(visible.length)}</div>
        </div>
        <div className="rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-3">
          <div className="text-xs text-gray-500 dark:text-gray-400">Tickets sold</div>
          <div className="text-lg font-semibold">{fmtInt(totals.tickets)}</div>
        </div>
        <div className="rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-3">
          <div className="text-xs text-gray-500 dark:text-gray-400">Revenue</div>
          <div className="text-lg font-semibold">{fmtGBP(totals.revenue)}</div>
        </div>
        <div
          className={
            'rounded-lg border p-3 ' +
            (totals.over > 0
              ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-800'
              : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700')
          }
        >
          <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
            {totals.over > 0 && (
              <IonIcon icon={warningOutline} className="text-red-500" />
            )}
            Over {threshold}
          </div>
          <div
            className={
              'text-lg font-semibold ' +
              (totals.over > 0 ? 'text-red-600 dark:text-red-400' : '')
            }
          >
            {fmtInt(totals.over)}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800">
        <div className="hidden sm:grid grid-cols-[1fr_90px_110px_80px_40px] gap-2 px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
          <div>Event</div>
          <div className="text-right">Tickets</div>
          <div className="text-right">Revenue</div>
          <div className="text-right">Orders</div>
          <div />
        </div>

        {visible.length === 0 && (
          <div className="p-6 text-center text-gray-500 dark:text-gray-400 text-sm">
            No events match.
          </div>
        )}

        {visible.map((e) => {
          const isOpen = expanded.has(e.eventName);
          const platforms = Object.entries(e.byPlatform).sort(
            (a, b) => b[1].tickets - a[1].tickets,
          );
          return (
            <div
              key={e.eventName}
              className="border-b border-gray-100 dark:border-gray-700/60 last:border-b-0"
            >
              <button
                onClick={() => toggle(e.eventName)}
                className={
                  'w-full text-left grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_90px_110px_80px_40px] gap-2 items-center px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors ' +
                  rowTone(e.tickets)
                }
              >
                <div className="min-w-0">
                  <div className="font-medium truncate text-gray-900 dark:text-gray-100">
                    {e.eventName}
                  </div>
                  {/* platform badges */}
                  <div className="mt-1 flex flex-wrap gap-1">
                    {platforms.map(([p, s]) => (
                      <span
                        key={p}
                        className={
                          'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium ' +
                          platformColor(p)
                        }
                        title={`${p}: ${fmtInt(s.tickets)} tickets · ${fmtGBP(s.revenue)} · ${fmtInt(s.orders)} orders`}
                      >
                        {p} {fmtInt(s.tickets)}
                      </span>
                    ))}
                  </div>
                </div>

                {/* mobile: compact tickets; desktop: separate cols */}
                <div className="sm:hidden text-right">
                  <div className={'font-semibold ' + ticketTextTone(e.tickets)}>
                    {fmtInt(e.tickets)} tix
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {fmtGBP(e.revenue)} · {fmtInt(e.orders)} ord
                  </div>
                </div>

                <div
                  className={'hidden sm:block text-right font-semibold ' + ticketTextTone(e.tickets)}
                >
                  {fmtInt(e.tickets)}
                </div>
                <div className="hidden sm:block text-right text-gray-700 dark:text-gray-300">
                  {fmtGBP(e.revenue)}
                </div>
                <div className="hidden sm:block text-right text-gray-500 dark:text-gray-400">
                  {fmtInt(e.orders)}
                </div>
                <div className="hidden sm:flex justify-end text-gray-400">
                  <IonIcon icon={isOpen ? chevronDownOutline : chevronForwardOutline} />
                </div>
              </button>

              {/* Expanded per-platform breakdown */}
              {isOpen && (
                <div className="px-3 pb-3 pt-1 bg-gray-50/60 dark:bg-gray-900/30 overflow-x-auto">
                  <table className="w-full text-sm min-w-[480px] sm:min-w-0">
                    <thead>
                      <tr className="text-xs text-gray-500 dark:text-gray-400">
                        <th className="text-left font-medium py-1">Platform</th>
                        <th className="text-right font-medium py-1">Tickets</th>
                        <th className="text-right font-medium py-1">Revenue</th>
                        <th className="text-right font-medium py-1">Orders</th>
                      </tr>
                    </thead>
                    <tbody>
                      {platforms.map(([p, s]) => (
                        <tr key={p} className="border-t border-gray-200 dark:border-gray-700/60">
                          <td className="py-1.5">
                            <span
                              className={
                                'inline-block px-1.5 py-0.5 rounded text-[11px] font-medium ' +
                                platformColor(p)
                              }
                            >
                              {p}
                            </span>
                          </td>
                          <td className="text-right py-1.5">{fmtInt(s.tickets)}</td>
                          <td className="text-right py-1.5">{fmtGBP(s.revenue)}</td>
                          <td className="text-right py-1.5 text-gray-500 dark:text-gray-400">
                            {fmtInt(s.orders)}
                          </td>
                        </tr>
                      ))}
                      <tr className="border-t-2 border-gray-300 dark:border-gray-600 font-semibold">
                        <td className="py-1.5">Total</td>
                        <td className="text-right py-1.5">{fmtInt(e.tickets)}</td>
                        <td className="text-right py-1.5">{fmtGBP(e.revenue)}</td>
                        <td className="text-right py-1.5">{fmtInt(e.orders)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EventSalesContent;
